import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { API_BASE_URL } from '../../config'
import { getToken } from '../authentication/apiAuthentication'

export const apiCloudSystemSlice = createApi({
  reducerPath: 'apiCloudSystem',
  baseQuery: fetchBaseQuery({ 
    baseUrl: `${API_BASE_URL}`,
    prepareHeaders: async (headers, { getState }) => {
      // getState for context
      const msalContextProgress = getState().apiAuth.inProgress

      let token = await getToken(msalContextProgress)
      if (token) {
        headers.set('authorization', `Bearer ${token}`)
      }

      return headers
    },
  }),
  endpoints: builder => ({
    getCloudSystem: builder.query({
      query: ({ serialNumber }) => `/v1/cloudsystem/${serialNumber}`
    }),
    postMetadata: builder.query({
      query: ({ serialNumber, ...cloudSystem }) => ({ url: `/v1/cloudsystem/${serialNumber}/metadata`, method: 'POST', body: cloudSystem }),
    }),
    postArchive: builder.query({
      query: ({ serialNumber }) => ({ url: `/v1/cloudsystem/${serialNumber}/archive`, method: 'POST'}),
    }),
    postBoxSwap: builder.query({
      query: ({ serialNumber, ...body }) => ({ url: `/v1/cloudsystem/${serialNumber}/boxswap`, method: 'POST', body: body }),
    }),
    postSoftwareUpdateCmd: builder.query({
      query: ({ serialNumber }) => ({ url: `/v1/cloudsystem/${serialNumber}/action/update-software`, method: 'POST' }),
    }),
    postContentUpdateCmd: builder.query({
      query: ({ serialNumber }) => ({ url: `/v1/cloudsystem/${serialNumber}/action/update-content`, method: 'POST' }),
    }),
    postRebootCmd: builder.query({
      query: ({ serialNumber }) => ({ url: `/v1/cloudsystem/${serialNumber}/action/reboot`, method: 'POST' }),
    }),
    postGetSyslogCmd: builder.query({
      query: ({ serialNumber }) => ({ url: `/v1/cloudsystem/${serialNumber}/action/get-syslog`, method: 'POST' }),
    }),
    postCancelCmd: builder.query({
      query: ({ serialNumber, ...requestBody }) => ({ url: `/v1/cloudsystem/${serialNumber}/action/cancel-action`, method: 'POST', body: requestBody }),
    }),
    postGetDeviceSettingsCmd: builder.query({
      query: ({ serialNumber }) => ({ url: `/v1/cloudsystem/${serialNumber}/action/get-settings`, method: 'POST' }),
    }),
    postUpdateDeviceSettingsCmd: builder.query({
      query: ( settings ) => ({ url: `/v1/cloudsystem/${settings.serialNumber}/action/change-settings`, method: 'POST', body: settings }),
    }),
  })
})

export const apiDownloadSyslog = async (serialNumber) => {
  const url = `${API_BASE_URL}/v1/cloudsystem/${serialNumber}/syslog`

  let headers = new Headers();
  let token = await getToken()
  if (token) {
    headers.set('authorization', `Bearer ${token}`)
  }

  // This is a kind of janky trick we need to do to trigger a download with auth headers attached
  const result = await fetch(url, { headers })

  // Get the filename from the response headers
  // Content-Disposition: attachmend; filename="{filename}"
  const disposition = result.headers.get('Content-Disposition')
  const match = disposition.match(/.*filename=\"(.*)\"/)
  const filename = match[1]

  const blob = await result.blob()

  const objectUrl = window.URL.createObjectURL(blob)

  let anchor = document.createElement("a");
  let element = document.body.appendChild(anchor);
  anchor.download = filename
  anchor.href = objectUrl
  anchor.click()
  document.body.removeChild(anchor)

  window.URL.revokeObjectURL(objectUrl);
}

export const {
  useGetCloudSystemQuery,
  usePostMetadataQuery,
  usePostArchiveQuery,
  usePostBoxSwapQuery,
  usePostSoftwareUpdateCmdQuery,
  usePostContentUpdateCmdQuery,
  usePostRebootCmdQuery,
  usePostGetSyslogCmdQuery,
  usePostCancelCmdQuery,
  usePostGetDeviceSettingsCmdQuery,
  usePostUpdateDeviceSettingsCmdQuery,
} = apiCloudSystemSlice